import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { DynamicTexture, Texture } from '@babylonjs/core/Materials/Textures';
import { Material } from '@babylonjs/core/Materials/material';
import { Scene } from '@babylonjs/core/scene';
import { getBinClampedSize, isIOS } from '@kb3d/common-util/texture-util';
import { instanceOf } from '@kb3d/models/classes/decorators/model-utils';
import { eMaterialWorkflow, eTextureChannel } from '@kb3d/models/classes/enums';
import { FeatureWithTextures, isFeatureNode } from '@kb3d/models/classes/features/feature';
import { FileTextureLayer } from '@kb3d/models/classes/materials/file-texture-layer';
import { MaterialNode, isMaterialNode } from '@kb3d/models/classes/materials/material-node';
import { TextureLayer } from '@kb3d/models/classes/materials/texture-layer';
import { Token } from '@kb3d/models/classes/token';
import { ITrackedClass, isTrackedClass } from '@kb3d/models/interfaces/tracked-class';
import { KbViewer } from '@kb3d/view/kb-viewer';
import { Subject, merge } from 'rxjs';
import { debounceTime, takeWhile, throttleTime } from 'rxjs/operators';
import { Renderer } from './renderer';
import { RendererManager } from './renderer-manager';
import { SamplingModeMap } from './scene';
import { FileTextureLayerRenderer } from './textures';
import { ColorFillTextureLayerRenderer } from './textures/color-fill-texture';
import { SvgTextureLayerRenderer } from './textures/svg-texture';
import { textureChannelMap, TextureLayerRenderer } from './textures/texture';

export type TextureParameters = {
    strength: number;
    hasTransparency: boolean;
    nativeSize: { width: number; height: number };
};

const maskingCanvas = document.createElement('canvas');

export class TextureStackRenderer extends Renderer<TextureLayer, Texture> {
    modelToken = Token.TextureLayer;

    queuedTexturesToProcess = new Map<MaterialNode | FeatureWithTextures, Map<eTextureChannel, TextureParameters>>();
    queueSize = new Subject<number>();
    renderers = new Map<Token, TextureLayerRenderer<any>>();
    noMipMapFallback = false;

    private updateTextureStackRequest = new Subject<void>();
    private deferCalled = false;

    constructor(viewer: KbViewer, scene: Scene, private rendererManager: RendererManager) {
        super(viewer, scene);
        const fileTexture = new FileTextureLayerRenderer(viewer, scene);
        const svgTexture = new SvgTextureLayerRenderer(viewer, scene);
        const colorTexture = new ColorFillTextureLayerRenderer(viewer, scene);
        this.renderers.set(Token.FileTextureLayer, fileTexture);
        this.renderers.set(Token.SvgTextureLayer, svgTexture);
        this.renderers.set(Token.ColorFillTextureLayer, colorTexture);

        /**
         * See render.ts comment - this delay must be added to the render as a change can take up to this delay time to be displayed.
         * We don't want to repeatedly processes texture changes coming in quick succession, which often happens if there are many textures being loaded at once.
         * */
        const throttleDebounceTime = 800;
        merge(
            this.updateTextureStackRequest.pipe(throttleTime(throttleDebounceTime)),
            this.updateTextureStackRequest.pipe(debounceTime(throttleDebounceTime))
        ).subscribe(() => {
            this.updateTextureStacksExec();
        });

        // This code works for forcing intel gpus to not use mipmaps, but some scenes don't have artifacting issues and need mipmaps. So
        // In the future we can introduce a scene setting to enable the intel fallback
        // if (this.viewer.glInfo.match(/Intel.*Iris.*Xe/)) {
        //     this.noMipMapFallback = true;
        // }
    }

    materialDeleted(node: MaterialNode) {
        for (const channel in eTextureChannel) {
            const textureId = this.getTexture3dId(node, channel as eTextureChannel);
            const texture = this.babylonMap.get(textureId);
            if (texture) {
                this.disposeTexture(texture);
                this.babylonMap.delete(this.getTexture3dId(node, channel as eTextureChannel));
            }
            this.babylonMap.delete(textureId);
        }
    }

    /** This should only be called from the editor. Has a very heavy performance impact. */
    forceRebuildTexture(materialNode: MaterialNode | FeatureWithTextures, channel?: eTextureChannel) {
        if (channel) {
            this.resetTextureStack(materialNode, channel);
        } else {
            for (const channel in eTextureChannel) {
                this.resetTextureStack(materialNode, channel as eTextureChannel);
            }
        }
        this.updateTextureStacksInternal(materialNode);
    }

    resetTextureStack(
        texturedNode: MaterialNode | FeatureWithTextures,
        channel: eTextureChannel,
        setParameters?: Partial<TextureParameters>
    ) {
        let material: Material | undefined;
        let materialNode: MaterialNode | undefined;

        if (isMaterialNode(texturedNode)) {
            materialNode = texturedNode;
            if (!materialNode._initialized) {
                return;
            }
            material = this.rendererManager.material.get3d(materialNode)!;
            if (!material) {
                // There might not be a material in babylon if the material was deleted prior to this renderer running.
                // console.warn(`material not generated from ${materialNode.name}`);
                return;
            }
        }

        const hasTextureInChannel = !!texturedNode.textures.find(t => t.channel === channel);
        if (!hasTextureInChannel) {
            return;
        }

        let channelsToProcess = this.queuedTexturesToProcess.get(texturedNode);
        if (!channelsToProcess) {
            channelsToProcess = new Map();
            this.queuedTexturesToProcess.set(texturedNode, channelsToProcess);
        }

        // Here we don't want to filter out textures with no data because they cam still be used to set the canvas size
        const textureLayers = this.getActiveLayers(texturedNode, channel);

        // Register that the channel needs to be processed.
        // Figure out the highest strength among the texture layers so we can
        // calculate the strength of each one as a fraction of that.
        let channelStrength = 0;
        textureLayers.forEach(textureLayer => {
            channelStrength = Math.max(channelStrength, textureLayer.strength);
        });

        const existingProcess = channelsToProcess.get(channel);

        const propName3d = textureChannelMap[channel as eTextureChannel] as keyof Material;
        const nativeSize = { width: 0, height: 0 };

        if (material && materialNode) {
            // Reset the material to re-run the texture stack (or clear the texture if there are no textures anymore)
            if (textureLayers.length === 0) {
                // If no textures are using this channel anymore, then remove it from the material
                if (Array.isArray(propName3d)) {
                    const res = getObjectPropertyAtPath(material, propName3d, false);
                    if (res.obj && res.obj[res.prop]) {
                        const texture = res.obj[res.prop];
                        if (texture instanceof Texture) {
                            this.disposeTexture(texture);
                            res.obj[res.prop] = null;
                            this.babylonMap.delete(this.getTexture3dId(materialNode, channel));
                        }
                    }
                } else {
                    if (material && material[propName3d]) {
                        (material[propName3d] as Texture).dispose();
                        (material[propName3d] as null) = null;
                        this.babylonMap.delete(this.getTexture3dId(materialNode, channel));
                    }
                }
            } else {
                const texture = this.getTexture(materialNode, channel);

                if (texture instanceof DynamicTexture) {
                    const context = texture.getContext();
                    
                    const tagetSize = this.calculateDynamicTextureSize(textureLayers[0]);
                    nativeSize.width = tagetSize.width / tagetSize.scalingW;
                    nativeSize.height = tagetSize.height / tagetSize.scalingH;
                    context.canvas.width = tagetSize.width;
                    context.canvas.height = tagetSize.height;

                    context.clearRect(0, 0, context.canvas.width, context.canvas.height);
                }
            }
        }

        channelsToProcess.set(channel, {
            strength: channelStrength,
            hasTransparency: materialNode ? materialNode.hasTransparency : false,
            ...(existingProcess || {}),
            ...(setParameters || {}),
            nativeSize,
        });
    }

    protected calculateDynamicTextureSize(texture: TextureLayer) {
        const baseImageSize = this.getRenderer(texture).getNativeSize(texture);
        const targetContextSize = { ...baseImageSize };

        // on ios use small textures for all non-albedo textures to save on memory
        if (isIOS() && texture.channel !== eTextureChannel.albedo) {
            targetContextSize.width = Math.min(128, targetContextSize.width);
            targetContextSize.height = Math.min(128, targetContextSize.height);
        }

        if (this.scene.getEngine().webGLVersion < 2) {
            const clampedSize = getBinClampedSize(targetContextSize.width, targetContextSize.height);
            targetContextSize.width = clampedSize;
            targetContextSize.height = clampedSize;
        }

        return {
            width: targetContextSize.width,
            height: targetContextSize.height,
            scalingW: targetContextSize.width / baseImageSize.width,
            scalingH: targetContextSize.height / baseImageSize.height,
        };
    }

    update3dInternal(feature: ITrackedClass<TextureLayer>) {
        if (isTrackedClass(feature)) {
            if (!feature.enabled || feature._suppressed) {
                if (feature.changes.has('enabled') || feature.changes.has('_suppressed')) {
                    this.delete3d(feature);
                }

                return;
            }

            const renderer = this.renderers.get(Token.withKey(feature.$type));
            if (!renderer) {
                throw Error('no renderer for feature');
            }
            if (feature._parent && isFeatureNode(feature._parent)) {
                feature.init(feature._parent);
            }
            renderer.update3d(feature);

            if (feature._parent && isFeatureNode(feature._parent) && isTrackedClass(feature._parent)) {
                feature._parent.setPropertyIsModified('textures', true);
            }

            if (feature._parent) {
                this.resetTextureStack(feature._parent, feature.channel);
            }
        }
    }

    updateTextureStacks() {
        if (!this.deferCalled && this.queuedTexturesToProcess.size > 0) {
            const queueLive = this.queueSize.pipe(takeWhile(size => size > 0));
            const p = queueLive.toPromise();
            this.viewer.deferSceneReady(p);
            this.deferCalled = true;
        }
        for (const node of this.queuedTexturesToProcess.keys()) {
            if (isFeatureNode(node) || node._initialized) {
                for (const texture of node.textures) {
                    if (!texture._init) {
                        texture.init(node);
                    }
                }
            }
        }
        this.updateTextureStackRequest.next();
    }

    private updateTextureStacksExec() {
        this.queueSize.next(this.queuedTexturesToProcess.size);

        for (const node of this.queuedTexturesToProcess.keys()) {
            this.updateTextureStacksInternal(node);

            if (this.queuedTexturesToProcess.size > 0) {
                this.updateTextureStacks();
            }
            // Low perf code to prevent iOS from erroring out. Doesn't seem necessary for now
            // return;
            // End low perf code
        }

        this.queueSize.next(this.queuedTexturesToProcess.size);
    }

    private updateTextureStacksInternal(node: MaterialNode | FeatureWithTextures) {
        if (this.queuedTexturesToProcess.has(node)) {
            if (isMaterialNode(node)) {
                const channels = this.queuedTexturesToProcess.get(node);
                const material = this.rendererManager.material.get3d(node);

                if (!channels || !material) {
                    return;
                }

                // Process texture stack
                channels.forEach((parameters, channel) => {
                    // Put the texture in the right place on the material
                    const propName3d = textureChannelMap[channel as eTextureChannel];
                    if (instanceOf(node, Token.AdvancedMaterialNode)) {
                        if (
                            node.workflowMode === eMaterialWorkflow.metallicRoughness &&
                            channel === eTextureChannel.glossiness
                        ) {
                            return;
                        } else if (
                            node.workflowMode === eMaterialWorkflow.specularGlossiness &&
                            channel === eTextureChannel.roughness
                        ) {
                            return;
                        }
                    }

                    const texture = this.getTexture(node, channel);
                    // If texture is undefined, that means that there is a missing setting/layers that means we don't need a texture for this channel
                    if (texture) {
                        // Paint the image onto the texture
                        // We don't want to count layers with no content in the stack, like file textures with no file or svg textures with no data
                        const textureLayers = this.getActiveLayers(node, channel).filter(node => {
                            const renderer = this.getRenderer(node);
                            return renderer.textureHasContent(node);
                        });

                        if (textureLayers.length > 0) {
                            // Put the texture in the right place on the material if it's not already
                            if (Array.isArray(propName3d)) {
                                const res = getObjectPropertyAtPath(material, propName3d);
                                if (res.obj[res.prop] !== texture) {
                                    res.obj[res.prop] = texture;
                                }
                            } else {
                                if (material[propName3d as keyof Material] !== texture) {
                                    (material[propName3d as keyof Material] as Texture) = texture;
                                }
                            }

                            if (texture instanceof DynamicTexture) {
                                const context = texture.getContext();

                                if (!(context instanceof CanvasRenderingContext2D)) {
                                    throw Error('Unexpected rendering context');
                                }
                                if (channel !== eTextureChannel.albedo) {
                                    // Fill texture with color that makes sense depending on the texture channel
                                    switch (channel) {
                                        case eTextureChannel.normal: {
                                            context.fillStyle = 'rgb(128, 128, 255)';
                                            break;
                                        }

                                        case eTextureChannel.opacity:
                                        case eTextureChannel.ambient:
                                        case eTextureChannel.ambientOcclusion: {
                                            context.fillStyle = 'rgb(255, 255, 255)';
                                            break;
                                        }

                                        default: {
                                            context.fillStyle = 'rgb(0, 0, 0)';
                                            break;
                                        }
                                    }

                                    context.fillRect(0, 0, context.canvas.width, context.canvas.height);
                                }
                                this.paintTextureOnCanvas(textureLayers, context, parameters);

                                updateTextureProps(texture, parameters);

                                texture.update(true); // invertY
                            } else {
                                // If texture is not a dynamic texture, then it's a basic texture and there is only one texture layer. We just have to make sure the path is correct
                                const fileTextureLayer = textureLayers[0];
                                if (!(fileTextureLayer instanceof FileTextureLayer)) {
                                    throw Error('Unexpected texture type');
                                } else {
                                    if (fileTextureLayer._parent instanceof MaterialNode) {
                                        const path = fileTextureLayer._bufferSource || '';
                                        if (texture.url !== path && fileTextureLayer._buffer) {
                                            texture.updateURL(path, (fileTextureLayer._buffer || null) as any);
                                        }
                                    }

                                    updateTextureProps(texture, parameters);
                                }
                            }
                        }
                    }
                });
            } else {
                if (isTrackedClass(node)) {
                    node.setPropertyIsModified('textures', true);
                    this.queuedTexturesToProcess.delete(node);
                    return;
                }
            }
        }
        if (isMaterialNode(node) && isTrackedClass(node)) {
            this.applyUvProperties(node);
        } else {
            throw Error('Material is not being tracked');
        }
        this.queuedTexturesToProcess.delete(node);

        function updateTextureProps(texture: Texture, parameters: TextureParameters) {
            texture.level = parameters.strength;
            texture.hasAlpha = parameters.hasTransparency;
        }
    }

    delete3d(feature: ITrackedClass<TextureLayer>) {
        if (feature._parent) {
            this.resetTextureStack(feature._parent, feature.channel);
        }

        const renderer = this.renderers.get(Token.withKey(feature.$type));
        if (!renderer) {
            throw Error('no renderer for feature');
        }
        renderer.delete3d(feature);
    }

    get3d(texture: TextureLayer) {
        if (!texture._parent) {
            return undefined;
        }
        if (texture._parent instanceof MaterialNode) {
            return this.babylonMap.get(this.getTexture3dId(texture._parent, texture.channel));
        }
    }

    set3d(texture: TextureLayer, babylonObject: Texture) {
        if (texture._parent) {
            if (texture._parent instanceof MaterialNode) {
                this.babylonMap.set(this.getTexture3dId(texture._parent, texture.channel), babylonObject);
            }
        }
    }

    dispose() {
        this.queuedTexturesToProcess.clear();
        this.renderers.forEach(renderer => renderer.dispose());
        this.updateTextureStackRequest.complete();
        super.dispose();
    }

    setTransparency(materialNode: MaterialNode | FeatureWithTextures, hasTransparency: boolean) {
        // TODO: limit this to only albedo textures for now. Will need testing.
        this.resetTextureStack(materialNode, eTextureChannel.albedo, { hasTransparency });
    }

    applyUvProperties(materialNode: ITrackedClass<MaterialNode>) {
        const material = this.rendererManager.material.get3d(materialNode);
        if (material) {
            for (const channel in textureChannelMap) {
                const firstTextureLayer = materialNode.textures.find(
                    t => t.channel === channel
                ) as ITrackedClass<TextureLayer>;

                const propName3d = textureChannelMap[channel as eTextureChannel] as keyof Material;

                let texture: Texture;
                if (Array.isArray(propName3d)) {
                    const res = getObjectPropertyAtPath(material, propName3d);
                    texture = res.obj[res.prop];
                } else {
                    texture = material[propName3d];
                }

                if (texture && firstTextureLayer) {
                    if (!(texture instanceof Texture)) {
                        throw Error(propName3d.toString() + ' is not a texture');
                    }
                    texture.uOffset = firstTextureLayer.uOffset;
                    texture.vOffset = firstTextureLayer.vOffset;
                    texture.uScale = firstTextureLayer.uScale;
                    texture.vScale = firstTextureLayer.vScale;
                    texture.uAng = (firstTextureLayer.uAngle * Math.PI) / 180;
                    texture.vAng = (firstTextureLayer.vAngle * Math.PI) / 180;
                    texture.wAng = (firstTextureLayer.wAngle * Math.PI) / 180;
                    texture.uRotationCenter = firstTextureLayer.uPivot;
                    texture.vRotationCenter = firstTextureLayer.vPivot;
                    texture.wRotationCenter = firstTextureLayer.wPivot;
                    // if (firstTextureLayer.uWrap === eTextureWrapAddressMode.clamp) {
                    //     texture.wrapU = Texture.CLAMP_ADDRESSMODE;
                    // } else {
                    //     texture.wrapU = Texture.WRAP_ADDRESSMODE;
                    // }
                    // if (firstTextureLayer.vWrap === eTextureWrapAddressMode.clamp) {
                    //     texture.wrapV = Texture.CLAMP_ADDRESSMODE;
                    // } else {
                    //     texture.wrapV = Texture.WRAP_ADDRESSMODE;
                    // }

                    // For some reason wrap addressmode is not getting converted correctly. Repeat is the safer option so
                    // Let's hardcode repeat for now
                    texture.wrapU = Texture.WRAP_ADDRESSMODE;
                    texture.wrapV = Texture.WRAP_ADDRESSMODE;

                    if (!(texture instanceof DynamicTexture)) {
                        texture.vScale *= -1;
                    }
                }
            }
        }
    }

    paintTextureOnCanvas(
        textureLayers: TextureLayer[],
        context: CanvasRenderingContext2D,
        parameters: TextureParameters
    ) {
        let clippingTexture: TextureLayer | null = null;

        for (let i = 0; i < textureLayers.length; i++) {
            if (!textureLayers[i]._init) {
                continue;
            }

            const textureLayer = textureLayers[i];
            const renderer = this.getRenderer(textureLayer);

            // Continue clipping with the same texture as long as useClippingMask is true.
            // When there's a texture that's not clipping, that can potentially be a new clipping mask.
            if (!textureLayer.useClippingMask) {
                clippingTexture = null;
            }

            const maskingCtx = maskingCanvas.getContext('2d', {
                willReadFrequently: true,
            })!;
            if (textureLayers[i + 1] && textureLayers[i + 1].useClippingMask && !clippingTexture) {
                maskingCanvas.width = context.canvas.width;
                maskingCanvas.height = context.canvas.height;
                clippingTexture = textureLayer;
            } else if (textureLayer.useClippingMask && clippingTexture) {
                const clippingTextureRenderer = this.getRenderer(clippingTexture);
                maskingCtx.clearRect(0, 0, maskingCanvas.width, maskingCanvas.height);
                clippingTextureRenderer.paintTexture(
                    clippingTexture,
                    maskingCtx,
                    { strength: 1, hasTransparency: true, nativeSize: parameters.nativeSize },
                    {}
                );
                // The clipped image will be scaled already, so we want to avoid double scaling the clipped images
                // Thus set the native size to the canvas size here
                renderer.paintTexture(
                    textureLayer,
                    context,
                    { ...parameters, nativeSize: { width: context.canvas.width, height: context.canvas.height } },
                    {
                    mask: { context: maskingCtx },
                    }
                );
            } else {
                renderer.paintTexture(textureLayer, context, parameters, {});
            }
        }
        return context;
    }

    getActiveLayers(
        materialNode: MaterialNode | FeatureWithTextures,
        channel: eTextureChannel,
        includeSupressed = false
    ) {
        return materialNode.textures.filter(
            texture => texture.channel === channel && texture.enabled && (includeSupressed || !texture._suppressed)
        );
    }

    getRenderer(textureLayer: TextureLayer) {
        const renderer = this.renderers.get(Token.withKey(textureLayer.$type));
        if (!renderer) {
            throw Error('no renderer for texture layer');
        }
        return renderer;
    }

    private canUseSimpleTexture(activeLayers: TextureLayer[]) {
        if (activeLayers.length === 1) {
            const layer = activeLayers[0];
            return layer instanceof FileTextureLayer;
        }
        return false;
    }

    private disposeTexture(texture: Texture) {
        texture.dispose();
    }

    private getTexture3dId(node: MaterialNode, channel: eTextureChannel) {
        return `${node._dynamicId}_${channel}`;
    }

    private getTexture(node: MaterialNode, channel: eTextureChannel) {
        const rootScene = this.viewer.sceneNode;
        const activeLayers = this.getActiveLayers(node, channel, true);
        const canUseSimpleTexture = this.canUseSimpleTexture(activeLayers);

        let texture = this.babylonMap.get(this.getTexture3dId(node, channel));
        const originalTexture = texture;

        if (originalTexture) {
            if (originalTexture instanceof DynamicTexture && canUseSimpleTexture) {
                this.disposeTexture(originalTexture);
            } else if (activeLayers.length > 1 && !(texture instanceof DynamicTexture)) {
                this.disposeTexture(originalTexture);
            } else {
                return originalTexture;
            }
        }

        if (canUseSimpleTexture) {
            const fileTexture = activeLayers[0] as FileTextureLayer;
            if (fileTexture.path && fileTexture._buffer && fileTexture._parent instanceof MaterialNode) {
                texture = new Texture(
                    fileTexture._bufferSource || '',
                    this.scene,
                    this.noMipMapFallback,
                    false,
                    SamplingModeMap[rootScene.textureSamplingMode],
                    null,
                    null,
                    fileTexture._buffer
                );
                texture.name = this.getTexture3dId(node, channel);
            } else {
                return undefined;
            }
        } else {
            texture = new DynamicTexture(
                this.getTexture3dId(node, channel),
                256,
                this.scene,
                !this.noMipMapFallback,
                SamplingModeMap[rootScene.textureSamplingMode]
            );
        }

        texture.homogeneousRotationInUVTransform = true;
        this.babylonMap.set(this.getTexture3dId(node, channel), texture);

        // set initial values
        if (originalTexture) {
            texture.hasAlpha = originalTexture.hasAlpha;
            texture.level = originalTexture.level;
            texture.getAlphaFromRGB = originalTexture.getAlphaFromRGB;
        } else {
            texture.hasAlpha = false;
            if (channel === eTextureChannel.opacity) {
                texture.getAlphaFromRGB = true;
            } else {
                texture.getAlphaFromRGB = false;
            }
            texture.level = 1;
        }
        texture.coordinatesIndex = 0;
        texture.coordinatesMode = 0;
        texture.anisotropicFilteringLevel = 4;

        if (texture instanceof Texture) {
            texture.uOffset = 0; // Define an offset on the texture to offset the u coordinates of the UVs
            texture.vOffset = 0; // Define an offset on the texture to offset the v coordinates of the UVs
            texture.uScale = -1; // Define an offset on the texture to scale the u coordinates of the UVs
            texture.vScale = 1; // Define an offset on the texture to scale the v coordinates of the UVs
            texture.uAng = 0; // Define an offset on the texture to rotate around the u coordinates of the UVs
            texture.vAng = 0; // Define an offset on the texture to rotate around the v coordinates of the UVs
            texture.wAng = 0; // Define an offset on the texture to rotate around the w coordinates of the UVs (in case of 3d texture)
            texture.uRotationCenter = 0.5; // Defines the center of rotation (U)
            texture.vRotationCenter = 0.5; // Defines the center of rotation (V)
            texture.wRotationCenter = 0.5; // Defin
            texture.isBlocking = false;
        }

        return texture;
    }
}

function getObjectPropertyAtPath<T>(_obj: T, path: string[], throwOnNotFound = true) {
    let obj = _obj as any;
    const propPath = path.slice().reverse();
    while (propPath.length > 1 && obj) {
        const prop = propPath.pop() as any;
        if (prop in obj) {
            obj = obj[prop];
        } else {
            obj = null;
        }
    }
    if (throwOnNotFound && !obj) {
        throw Error('Texture property not found for ' + path.toString());
    }

    return { obj, prop: propPath[0] as string };
}
